import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import rolesApi from "../api/rolesApi";

/**
 * Create OR edit a role. Routes:
 *   /roles/new       → create mode
 *   /roles/:id       → edit mode
 *
 * Editing rules mirror the server:
 *   • SuperAdmin role: permission grid fully disabled (SUPERADMIN_PERMS_LOCKED).
 *     Name/description also disabled (it's a system role).
 *   • Other system roles (Admin): name/description disabled (system role),
 *     but permissions ARE editable.
 *   • Custom roles: everything editable.
 *
 * Permissions are grouped by area into a checkbox grid sourced from the
 * /permissions catalog so the UI always matches what the code enforces.
 */
export default function RoleEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isCreate = !id || id === "new";

  const [catalog, setCatalog] = useState([]); // [{area, code}]
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [role, setRole] = useState(null); // loaded role (edit mode)

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const isSuperAdmin = role?.name === "SuperAdmin";
  const isSystem = role?.isSystem === true;

  const showToast = (msg, tone = "info") => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 3000);
  };

  // Group catalog by area for rendering: { area: [code, code, ...] }
  const grouped = useMemo(() => {
    const map = {};
    for (const entry of catalog) {
      (map[entry.area] = map[entry.area] || []).push(entry.code);
    }
    return map;
  }, [catalog]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const catRes = await rolesApi.getPermissionCatalog();
        setCatalog(Array.isArray(catRes.data) ? catRes.data : []);

        if (!isCreate) {
          const roleRes = await rolesApi.get(id);
          const r = roleRes.data;
          setRole(r);
          setName(r.name || "");
          setDescription(r.description || "");
          setSelected(new Set(r.permissions || []));
        }
      } catch (err) {
        setError(err?.response?.data?.message || err.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const togglePermission = (code) => {
    if (isSuperAdmin) return; // locked
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  const toggleArea = (area, codes) => {
    if (isSuperAdmin) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = codes.every((c) => next.has(c));
      codes.forEach((c) => (allSelected ? next.delete(c) : next.add(c)));
      return next;
    });
  };

  const mapError = (err) => {
    const code = err?.response?.data?.errorCode;
    switch (code) {
      case "ROLE_NAME_TAKEN":
        return "A role with that name already exists.";
      case "SUPERADMIN_PERMS_LOCKED":
        return "SuperAdmin permissions are managed by the system and can't be edited.";
      case "SYSTEM_ROLE_PROTECTED":
        return "System roles can't be renamed.";
      case "UNKNOWN_PERMISSION":
        return "One or more selected permissions are invalid.";
      case "ROLE_NAME_REQUIRED":
      case "ROLE_NAME_LENGTH":
        return "Role name must be 2–64 characters.";
      default:
        return err?.response?.data?.message || "Save failed.";
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      if (isCreate) {
        const { data } = await rolesApi.create({
          name: name.trim(),
          description: description.trim() || null,
          permissions: Array.from(selected),
        });
        showToast("Role created");
        navigate(`/roles/${data.id}`, { replace: true });
        // reload into edit mode
        setRole(data);
      } else {
        // Update name/description (skip if system role — those fields are disabled)
        if (!isSystem) {
          await rolesApi.update(id, {
            name: name.trim(),
            description: description.trim() || null,
          });
        }
        // Update permissions (skip entirely for SuperAdmin — server rejects)
        if (!isSuperAdmin) {
          await rolesApi.setPermissions(id, Array.from(selected));
        }
        showToast("Role saved");
        // refresh the role to reflect server state
        const roleRes = await rolesApi.get(id);
        setRole(roleRes.data);
        setSelected(new Set(roleRes.data.permissions || []));
      }
    } catch (err) {
      setError(mapError(err));
    } finally {
      setSaving(false);
    }
  };

  const nameDisabled = isSystem;       // system roles can't be renamed
  const permsDisabled = isSuperAdmin;  // SuperAdmin perms locked

  return (
    <div className="shell">
      <header className="hero">
        <div>
          <p className="eyebrow">Access Control</p>
          <h1>{isCreate ? "Create Role" : `Edit Role`}</h1>
          <p className="lede">
            {isSuperAdmin
              ? "SuperAdmin always has every permission. Its permissions are managed by the system."
              : isSystem
              ? "This is a system role. Its name is fixed, but you can adjust its permissions."
              : "Define the role name and the permissions it grants."}
          </p>
        </div>
        <div className="hero-right">
          <div className="actions">
            <button className="ghost" onClick={() => navigate("/roles")}>
              Back to Roles
            </button>
          </div>
        </div>
      </header>

      {toast && <div className={`toast ${toast.tone}`}>{toast.msg}</div>}
      {error && <div className="toast error">{error}</div>}

      {loading ? (
        <div className="card">Loading…</div>
      ) : (
        <>
          <section className="card stack" style={{ marginBottom: 18 }}>
            <label>
              Role name
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={nameDisabled}
                placeholder="e.g. Auditor"
                maxLength={64}
              />
            </label>
            <label>
              Description
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={nameDisabled}
                placeholder="Short description of what this role is for"
                maxLength={512}
              />
            </label>
          </section>

          <section className="table-wrap">
            <div className="table-head">
              <div>
                <p className="eyebrow">Permissions</p>
                <h3>{selected.size} selected</h3>
                <p className="hint">
                  {permsDisabled
                    ? "Locked — SuperAdmin owns every permission."
                    : "Tap an area header to toggle the whole group."}
                </p>
              </div>
            </div>

            <div className="perm-groups">
              {Object.entries(grouped).map(([area, codes]) => {
                const allChecked = codes.every((c) => selected.has(c));
                const someChecked = codes.some((c) => selected.has(c));
                return (
                  <div className="card perm-group" key={area}>
                    <div className="perm-group-head">
                      <label
                        className="perm-area"
                        style={{ cursor: permsDisabled ? "not-allowed" : "pointer" }}
                      >
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={(el) => {
                            if (el) el.indeterminate = !allChecked && someChecked;
                          }}
                          onChange={() => toggleArea(area, codes)}
                          disabled={permsDisabled}
                        />
                        <span className="perm-area-name">{area}</span>
                      </label>
                      <span className="hint">
                        {codes.filter((c) => selected.has(c)).length}/{codes.length}
                      </span>
                    </div>

                    <div className="perm-list">
                      {codes.map((code) => (
                        <label
                          className="perm-item"
                          key={code}
                          style={{ cursor: permsDisabled ? "not-allowed" : "pointer" }}
                        >
                          <input
                            type="checkbox"
                            checked={selected.has(code)}
                            onChange={() => togglePermission(code)}
                            disabled={permsDisabled}
                          />
                          <span>{code}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="actions" style={{ marginTop: 18 }}>
            <button
              className="primary"
              onClick={handleSave}
              disabled={saving || (isSuperAdmin && isSystem && !isCreate && nameDisabled && permsDisabled)}
            >
              {saving ? "Saving..." : isCreate ? "Create Role" : "Save Changes"}
            </button>
            <button className="ghost" onClick={() => navigate("/roles")} disabled={saving}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}